import type { EgkAttestationHandle } from "@ascleo/frontdesk-contracts/patient-info/egk-attestation";
import type { ReviewContact } from "@ascleo/frontdesk-contracts/patient-info/review";
import {
  type FieldOutcome,
  type FieldResult,
  isTerminalOutcome,
} from "@ascleo/frontdesk-contracts/patient-info/submit";
import { eligibleContactKind } from "@ascleo/frontdesk-contracts/patient-info/contact-assertion";
import type { PatientInfoSubmitRequest } from "@ascleo/frontdesk-contracts/patient-info/submit";
import type { StagedSummary } from "./staged-changes";

/**
 * How one submitted field should be described back to the patient.
 *
 * Captured at submit time rather than re-read from the form afterwards: the result screen must
 * describe *what was sent*, and the form keeps changing underneath it.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const entry: SubmittedEntry = {
 *   key: "item:itm-0",
 *   category: "email",
 *   typeKey: "EMAILPRIV",
 *   displayValue: "neu@praxis.de",
 *   kind: "edit",
 * };
 * ```
 */
export interface SubmittedEntry {
  readonly key: string;
  readonly category: "phone" | "email" | "hausarzt";
  /** Contact-type key for i18n, absent for Hausarzt. */
  readonly typeKey?: string;
  /**
   * What to print for this entry. A deletion prints nothing — the patient asked for the value to be
   * gone, so repeating it on the result screen would undo the point of staging the removal.
   */
  readonly displayValue?: string;
  readonly kind: "edit" | "add" | "delete" | "proposal" | "hausarzt";
}

/**
 * The one proposal that was sent, frozen so a retry can resend it byte-for-byte.
 *
 * This exists because of how the server enforces one-proposal-per-capability: the durable ledger is
 * keyed on `submissionId` **plus a fingerprint over the full sorted operation set**, and a resume
 * carrying a different fingerprint is refused. So a retry cannot send "just the failed field" — it
 * must resend the identical request, and the server replays the outcomes it already made durable
 * while re-attempting only the non-terminal ones. Rebuilding the request from current form state
 * would shift the fingerprint and get the retry rejected.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * const proposal: SubmittedProposal = { request, entries: [] };
 * ```
 */
export interface SubmittedProposal {
  readonly request: PatientInfoSubmitRequest;
  readonly entries: readonly SubmittedEntry[];
}

/** The stable key a {@link FieldResult} refers to. */
function resultKey(result: FieldResult): string {
  if (result.field === "hausarzt") return "hausarzt";
  if (result.clientItemId !== undefined) return `client:${result.clientItemId}`;
  return `item:${result.itemId ?? ""}`;
}

/**
 * Build the frozen proposal for one commit: the exact wire request plus how to describe each field.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * buildSubmittedProposal({ handle, submissionId: "sub-1", staged, contacts: [] }).entries;
 * ```
 */
export function buildSubmittedProposal(input: {
  readonly handle: EgkAttestationHandle;
  readonly submissionId: string;
  readonly staged: StagedSummary;
  readonly contacts: readonly ReviewContact[];
}): SubmittedProposal {
  const byId = new Map(input.contacts.map((contact) => [contact.itemId, contact]));
  const guarded = new Set(
    input.contacts
      .filter((contact) => contact.protection !== undefined)
      .map((contact) => contact.itemId),
  );

  const entries: SubmittedEntry[] = input.staged.operations.map((operation) => {
    if (operation.kind === "add") {
      return {
        key: `client:${operation.clientItemId}`,
        category: eligibleContactKind(operation.type),
        typeKey: operation.type,
        displayValue: operation.value,
        kind: "add",
      };
    }
    const contact = byId.get(operation.itemId);
    const category =
      contact === undefined ? "phone" : eligibleContactKind(contact.type);
    const base = {
      key: `item:${operation.itemId}`,
      category,
      ...(contact === undefined ? {} : { typeKey: contact.type }),
    };
    if (operation.kind === "delete") return { ...base, kind: "delete" as const };
    return {
      ...base,
      displayValue: operation.value,
      kind: guarded.has(operation.itemId) ? ("proposal" as const) : ("edit" as const),
    };
  });

  if (input.staged.hausarzt.changed) {
    entries.push({
      key: "hausarzt",
      category: "hausarzt",
      ...(input.staged.hausarzt.value === null
        ? {}
        : { displayValue: input.staged.hausarzt.value }),
      kind: "hausarzt",
    });
  }

  return {
    request: {
      egkAttestation: input.handle,
      submissionId: input.submissionId,
      operations: input.staged.operations,
      ...(input.staged.hausarzt.changed
        ? { hausarzt: input.staged.hausarzt.value }
        : {}),
    },
    entries,
  };
}

/**
 * Merge a fresh attempt's results over what is already known.
 *
 * Terminal outcomes are replayed unchanged by the server, so a later attempt can only move a
 * `pending-retry` forward — never undo a field that already succeeded.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * mergeOutcomes(new Map(), [{ itemId: "itm-0", outcome: "updated" }]).get("item:itm-0");
 * // => "updated"
 * ```
 */
export function mergeOutcomes(
  known: ReadonlyMap<string, FieldOutcome>,
  results: readonly FieldResult[],
): ReadonlyMap<string, FieldOutcome> {
  const merged = new Map(known);
  for (const result of results) {
    const key = resultKey(result);
    const existing = merged.get(key);
    // A terminal outcome is the server's final word; a later attempt cannot walk it back.
    if (existing !== undefined && isTerminalOutcome(existing)) continue;
    merged.set(key, result.outcome);
  }
  return merged;
}

/**
 * Whether anything still needs the patient's attention.
 *
 * @since 1.2.0
 * @example
 * ```ts
 * hasUnfinishedOutcome(new Map([["item:itm-0", "pending-retry"]])); // => true
 * ```
 */
export function hasUnfinishedOutcome(
  outcomes: ReadonlyMap<string, FieldOutcome>,
): boolean {
  return [...outcomes.values()].some(
    (outcome) => outcome === "pending-retry" || outcome === "not-saved",
  );
}
